import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { blogService } from '../../services/blog.service';
import { FaArrowLeft, FaHeart, FaRegHeart, FaComment, FaEye, FaClock, FaUser, FaCalendar, FaGlobe, FaLock } from 'react-icons/fa';
import { authService } from '../../services/auth.service';

const ViewBlog = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [comment, setComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const currentUser = authService.getCurrentUser();

  useEffect(() => {
    if (id) {
      fetchBlog();
    }
  }, [id]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const data = await blogService.getBlog(id);
      
      if (data && data.blog) {
        setBlog(data.blog);
        
        // Check if current user has liked
        if (currentUser && data.blog.likes) {
          setIsLiked(data.blog.likes.includes(currentUser.id));
        }
        setLikesCount(data.blog.likes?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
      toast.error('Failed to load blog');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    try {
      const data = await blogService.toggleLike(id);
      setIsLiked(data.isLiked);
      setLikesCount(data.likesCount);
      toast.success(data.isLiked ? 'Blog liked!' : 'Blog unliked');
    } catch (error) {
      toast.error('Failed to toggle like');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    try {
      setSubmittingComment(true);
      await blogService.addComment(id, comment);
      setComment('');
      toast.success('Comment added!');
      fetchBlog(); // Refresh to show new comment
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <p className="text-xl text-gray-600 mb-4">Blog not found</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <FaArrowLeft /> Back
        </button>

        {/* Main Content */}
        <article className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Featured Image */}
          {blog.featuredImage && (
            <img
              src={blog.featuredImage}
              alt={blog.title}
              className="w-full h-96 object-cover"
              onError={(e) => e.target.style.display = 'none'}
            />
          )}

          <div className="p-8">
            {/* Status Badges */}
            <div className="mb-4 flex gap-2">
              {blog.status !== 'published' && (
                <span className={`px-3 py-1 rounded-full text-sm ${
                  blog.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                  blog.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  blog.status === 'approved' ? 'bg-green-100 text-green-800' :
                  'bg-red-100 text-red-800'
                } capitalize`}>
                  {blog.status}
                </span>
              )}
              
              {blog.isPublic ? (
                <span className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-800 flex items-center gap-1">
                  <FaGlobe /> Public
                </span>
              ) : (
                <span className="px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800 flex items-center gap-1">
                  <FaLock /> Internal
                </span>
              )}

              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm capitalize">
                {blog.category}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl font-bold mb-4">{blog.title}</h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-6 text-gray-600 text-sm mb-6 pb-6 border-b">
              <div className="flex items-center gap-2">
                <FaUser />
                <span>{blog.authorName}</span>
              </div>
              <div className="flex items-center gap-2">
                <FaCalendar />
                <span>{new Date(blog.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <FaClock />
                <span>{blog.readTime || 5} min read</span>
              </div>
              <div className="flex items-center gap-2">
                <FaEye />
                <span>{blog.views || 0} views</span>
              </div>
            </div>

            {/* Content */}
            <div className="prose max-w-none mb-8">
              <div className="text-lg leading-relaxed whitespace-pre-wrap text-gray-800">
                {blog.content}
              </div>
            </div>

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="mb-8 pb-8 border-b">
                <div className="flex flex-wrap gap-2">
                  {blog.tags.map((tag, index) => (
                    <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Like Button */}
            <div className="mb-8 pb-8 border-b">
              <button
                onClick={handleLike}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-colors ${
                  isLiked 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {isLiked ? <FaHeart /> : <FaRegHeart />}
                <span>{likesCount} {likesCount === 1 ? 'Like' : 'Likes'}</span>
              </button>
            </div>

            {/* Comments Section */}
            {blog.allowComments && (
              <div>
                <h3 className="text-2xl font-bold mb-6">
                  Comments ({blog.comments?.length || 0})
                </h3>

                {/* Add Comment Form */}
                <form onSubmit={handleComment} className="mb-8">
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="4"
                    placeholder="Write your comment..."
                    required
                  ></textarea>
                  <button
                    type="submit"
                    disabled={submittingComment}
                    className="mt-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    <FaComment />
                    {submittingComment ? 'Posting...' : 'Post Comment'}
                  </button>
                </form>

                {/* Comments List */}
                <div className="space-y-4">
                  {blog.comments && blog.comments.length > 0 ? (
                    blog.comments.map((c, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold">{c.userName}</span>
                          <span className="text-sm text-gray-500">
                            {new Date(c.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-gray-700">{c.comment}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-8">
                      No comments yet. Be the first to comment!
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </article>
      </div>
    </div>
  );
};

export default ViewBlog;
